import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs';
import { ChecklistRequirementsComponent } from './compliance-details-tabs/checklist-requirements/checklist-requirements.component';
import { AssignedAssetsComponent } from './compliance-details-tabs/assigned-assets/assigned-assets.component';
import { ComplianceAssignModalComponent } from '../../modals/compliance-assign-modal/compliance-assign-modal.component';
import { AddComplianceRequirementsModalComponent } from '../../modals/add-compliance-requirements-modal/add-compliance-requirements-modal.component';
import { ComplianceStateService } from '../../../../_shared/services/compliance-state.service';
import { SearchComponent } from '../../../../../../_shared/components/filters/search/search.component';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { AssetType } from '../../../../../../asset-management/_shared/enums/asset-type.enum';
import { AssetTypeFilterComponent } from '../../../../../../_shared/components/filters/asset-type-filter/asset-type-filter.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { translateComplianceScheduleName } from '../../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-compliance-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoDirective,
    ChecklistRequirementsComponent,
    AssignedAssetsComponent,
    SearchComponent,
    NgbDropdownModule,
    AssetTypeFilterComponent
  ],
  templateUrl: './compliance-details.component.html',
  styleUrl: './compliance-details.component.scss'
})
export class ComplianceDetailsComponent implements OnInit {
  @ViewChild(AssignedAssetsComponent) assignedAssetsRef!: AssignedAssetsComponent;
  @ViewChild('requirementsChecklist') requirementsChecklistRef?: ChecklistRequirementsComponent;

  private route = inject(ActivatedRoute);
  private modalService = inject(NgbModal);
  private stateService = inject(ComplianceStateService);
  private transloco = inject(TranslocoService);

  complianceId: number = 0;
  complianceName: string = '';
  complianceType: string = '';
  checklistSubType: string = '';
  checklistSubTypeLabel: string = '';
  title: string = '';
  activeTab: string = 'requirements';

  accountIds: number[] = [];
  searchTerm: string = '';
  selectedAssetType: AssetType | null = null;

  ngOnInit(): void {
    this.complianceId = Number(this.route.snapshot.queryParamMap.get('complianceId')) || 0;
    this.complianceName = this.route.snapshot.queryParamMap.get('complianceName') || '';
    this.complianceType = this.route.snapshot.queryParamMap.get('complianceType') || '';
    this.checklistSubType = this.route.snapshot.queryParamMap.get('checklistSubType') || '';
    this.checklistSubTypeLabel = translateComplianceScheduleName(this.transloco, this.checklistSubType);
    this.title = this.complianceName + (this.checklistSubTypeLabel ? ' - ' + this.checklistSubTypeLabel.replace('/', '&') : '');

    this.stateService.request$.pipe(take(1)).subscribe(request => {
      this.accountIds = request.accountIds ?? [];
    });

    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'assignedAssets' || tab === 'requirements' || tab === 'archive') {
      this.activeTab = tab;
    }
  }

  public toggleTab(tab: any): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
      this.searchTerm = '';
      this.selectedAssetType = null;
    }
  }

  onSearchChanged(term: string): void {
    this.searchTerm = term;
  }

  onAssetTypeChanged(type: AssetType | null): void {
    this.selectedAssetType = type;
  }

  onAddComplianceRequirementsClick(): void {
    const modalRef = this.modalService.open(AddComplianceRequirementsModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'add-compliance-requirements-modal-window'
    });
    modalRef.componentInstance.complianceId = this.complianceId;
    modalRef.closed.pipe(take(1)).subscribe((saved: boolean) => {
      if (saved) {
        this.requirementsChecklistRef?.refreshChecklistsAsync();
      }
    });
  }

  onAssignAssetsClick(): void {
    const modalRef = this.modalService.open(ComplianceAssignModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'lg'
    });
    modalRef.componentInstance.compliance = {
      id: this.complianceId,
      complianceName: this.complianceName,
      checklistSubType: this.checklistSubType,
      checklistSubTypeLabel: this.checklistSubTypeLabel
    };
    modalRef.componentInstance.accountIds = this.accountIds;
    modalRef.closed.subscribe((assigned: boolean) => {
      if (!assigned) return;
      if (this.activeTab === 'assignedAssets') {
        this.assignedAssetsRef?.loadAssignedAssetsAsync();
      } else {
        this.activeTab = 'assignedAssets';
      }
    });
  }
}